import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCrumbs } from "@/context/CrumbContext";
import { apiClient } from "@/context/AuthContext";
import PlanCanvas from "@/components/PlanCanvas";
import ResultsPanel from "@/components/ResultsPanel";
import AssistantPanel from "@/components/AssistantPanel";
import PlanAiPanel from "@/components/PlanAiPanel";
import EvidencePanel from "@/components/EvidencePanel";
import ShareModal from "@/components/ShareModal";
import MembersPanel from "@/components/MembersPanel";
import SignReportModal from "@/components/SignReportModal";
import ActionEditModal from "@/components/ActionEditModal";
import {
  AlertTriangle, ArrowLeft, Building2, Camera, Check, ChevronDown, ChevronRight, Download, FileText,
  ImagePlus, Loader2, Lock, Map as MapIcon, Paperclip, Pen, PenLine, Share2, Sparkles, Upload, Users, X,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Özet" },
  { id: "findings", label: "Bulgular" },
  { id: "plans", label: "Kroki & pinler" },
  { id: "assistant", label: "Asistan (AI)" },
  { id: "members", label: "Üyeler" },
  { id: "reports", label: "Raporlar" },
];

const statusClass = (value) => value === "Uygun" ? "status-good" : value === "Uygunsuz" ? "status-bad" : "status-warn";

export default function AssessmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [toast, setToast] = useState("");
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activePlanId, setActivePlanId] = useState(null);
  const [aiPlan, setAiPlan] = useState(null);
  const [canvasVersion, setCanvasVersion] = useState(0);
  const [filter, setFilter] = useState("all");
  const [expandedFinding, setExpandedFinding] = useState(null);
  const [shareReport, setShareReport] = useState(null);
  const [signReport, setSignReport] = useState(null);
  const [editFinding, setEditFinding] = useState(null);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/assessments/${id}`);
      setData(data);
      if (!activePlanId && data.plans?.length) setActivePlanId(data.plans[0].id);
    } catch (err) {
      setToast(err?.response?.data?.detail || "Değerlendirme yüklenemedi");
    }
  }, [id, activePlanId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { (async () => {
    try { const { data } = await apiClient.get("/auth/me"); setMe(data); } catch { /* ignore */ }
  })(); }, []);

  const isOwner = !!(me && data && data.assessment.owner_user_id === me.user_id);

  const verifyFinding = async (fid, action) => {
    try {
      await apiClient.post(`/assessments/${id}/findings/${fid}/verify`, { action });
      await load();
      setToast(action === "confirm" ? "Bulgu uygun olarak işaretlendi" : "Bulgu uygunsuz olarak işaretlendi");
    } catch { setToast("Doğrulama kaydedilemedi"); }
  };

  const handleUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data: plan } = await apiClient.post(`/assessments/${id}/plans`, form);
      setToast(`Kroki yüklendi: ${plan.file_name}`);
      await load();
      setActivePlanId(plan.id);
      setTab("plans");
    } catch (err) {
      setToast(err?.response?.data?.detail || "Kroki yüklenemedi");
    } finally { setUploading(false); ev.target.value = ""; }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const { data: rep } = await apiClient.post(`/assessments/${id}/reports`);
      setToast(`Rapor üretildi: ${rep.code}`);
      await load();
      setTab("reports");
    } catch (err) {
      setToast(err?.response?.data?.detail || "Rapor üretilemedi");
    } finally { setGenerating(false); }
  };

  const downloadReport = async (reportId, code) => {
    try {
      const res = await apiClient.get(`/reports/${reportId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `hazmatcore-${code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { setToast("Rapor indirilemedi"); }
  };

  const summary = useMemo(() => {
    if (!data) return null;
    const s = { total: data.findings.length, ok: 0, bad: 0, missing: 0, field: 0 };
    for (const f of data.findings) {
      const st = f.status;
      if (st === "Uygun") s.ok++;
      else if (st === "Uygunsuz") s.bad++;
      else if (st === "Sahada doğrulanmalı") s.field++;
      else s.missing++;
    }
    s.pct = s.total ? Math.round((s.ok / s.total) * 100) : 0;
    return s;
  }, [data]);

  const findingsFiltered = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.findings;
    if (filter === "pending") return data.findings.filter((f) => !f.resolved);
    if (filter === "ok") return data.findings.filter((f) => f.status === "Uygun");
    if (filter === "bad") return data.findings.filter((f) => f.status === "Uygunsuz");
    if (filter === "overrides") return data.findings.filter((f) => f.authority_override);
    return data.findings;
  }, [data, filter]);

  const pendingBlockers = useMemo(() => {
    if (!data) return [];
    const list = [];
    const pending = data.findings.filter((f) => !f.resolved).length;
    const aiPending = (data.plans || []).length ? null : null; // computed below if we had suggestions
    if (pending) list.push(`${pending} doğrulanmamış bulgu`);
    if (!data.plans?.length) list.push("Hiç kroki yüklenmemiş");
    return list;
  }, [data]);

  useCrumbs([{ label: "Değerlendirmeler", to: "/assessments" }, { label: data?.assessment?.title || "…" }]);
  const activePlan = data?.plans?.find((p) => p.id === activePlanId) || data?.plans?.[0];

  if (!data) return <div className="auth-splash" data-testid="assessment-loading"><Loader2 className="spin" size={22}/> <span>Değerlendirme yükleniyor…</span></div>;

  const { assessment, building, project, authority, class: cls, rules } = data;

  return (
    <div className="detail-shell" data-testid="assessment-detail">
      <header className="detail-topbar">
        <button className="icon-button" onClick={() => navigate("/assessments")} data-testid="back-to-dashboard"><ArrowLeft size={17}/></button>
        <div className="detail-head">
          <div className="eyebrow">DEĞERLENDİRME · {assessment.status}</div>
          <h1 data-testid="detail-title">{assessment.title}</h1>
          <small>
            <Building2 size={13}/> {building?.name} · {cls?.name} · {building?.hazard_class || "orta"} tehlike
            {authority && authority.id !== "none" && <> · <span className="auth-chip">{authority.short}</span></>}
          </small>
        </div>
        <div className="detail-actions">
          <button className="secondary-btn" onClick={() => navigate(`/saha/${id}`)} data-testid="field-mode-button">
            <Camera size={14}/> Saha modu
          </button>
          <label className="secondary-btn upload-button" data-testid="detail-upload-plan">
            {uploading ? <Loader2 size={14} className="spin"/> : <Upload size={14}/>} Kroki yükle
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleUpload} disabled={uploading}/>
          </label>
          <button className="primary-btn" onClick={generateReport} disabled={generating || summary.total === 0 || (summary.missing + summary.field > 0)} data-testid="generate-report-button">
            {generating ? <Loader2 size={14} className="spin"/> : <FileText size={14}/>} Taslak rapor üret
          </button>
        </div>
      </header>

      <div className="detail-tabs" data-testid="detail-tabs">
        {TABS.map((t) => (
          <button key={t.id} className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}>
            {t.label}
            {t.id === "findings" && <span className="count">{data.findings.length}</span>}
            {t.id === "plans" && <span className="count">{data.plans.length}</span>}
            {t.id === "reports" && <span className="count">{data.reports.length}</span>}
          </button>
        ))}
      </div>

      <div className="detail-body">
        {tab === "overview" && (
          <section className="detail-grid">
            <div className="section-block">
              <div className="section-title"><div><div className="eyebrow">METRİKLER</div><h2>Uyumluluk durumu</h2></div><span className="version-tag">{assessment.legislation_snapshot}</span></div>
              <div className="overview-metrics">
                <div><strong>{summary.pct}%</strong><small>Uyum oranı</small></div>
                <div><strong>{summary.ok}</strong><small>Uygun</small></div>
                <div className="warn"><strong>{summary.missing + summary.field}</strong><small>Doğrulama bekliyor</small></div>
                <div className="bad"><strong>{summary.bad}</strong><small>Uygunsuz</small></div>
                <div><strong>{data.plans.length}</strong><small>Kroki</small></div>
                <div><strong>{data.pins.length}</strong><small>İşaret</small></div>
              </div>
            </div>
            <div className="section-block">
              <div className="section-title"><div><div className="eyebrow">RAPOR DURUMU</div><h2>Taslak hazırlığı</h2></div><span className="version-tag">{assessment.last_revision || "R00"}</span></div>
              <div className="blocker-list">
                {pendingBlockers.length === 0 && <div className="blocker-item ok"><Check size={14}/><p>Rapor üretilebilir</p></div>}
                {pendingBlockers.map((b, i) => <div key={i} className="blocker-item"><span>{i+1}</span><p>{b}</p><AlertTriangle size={14}/></div>)}
              </div>
              {authority && authority.id !== "none" && (
                <div className="auth-detail" data-testid="authority-detail">
                  <strong>{authority.name}</strong>
                  <small>{authority.description}</small>
                  <small>{authority.overrides.length} ek şart uygulanıyor</small>
                </div>
              )}
            </div>
          </section>
        )}
        {tab === "overview" && (
          <ResultsPanel assessmentId={id} latestReport={data.reports[0]} onDownloadReport={downloadReport} onToast={setToast} version={data.findings.map((f) => f.status).join()}/>
        )}

        {tab === "assistant" && <AssistantPanel assessmentId={id}/>}

        {tab === "findings" && (
          <section className="section-block">
            <div className="section-title">
              <div><div className="eyebrow">BULGU LİSTESİ</div><h2>{findingsFiltered.length} / {data.findings.length} bulgu</h2></div>
              <div className="filter-row">
                {[["all","Tümü"],["pending","Bekleyen"],["ok","Uygun"],["bad","Uygunsuz"],["overrides","Yerel ek"]].map(([k,l]) => (
                  <button key={k} className={filter === k ? "chip active" : "chip"} onClick={() => setFilter(k)} data-testid={`filter-${k}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="findings-list detail-findings" data-testid="detail-findings-list">
              {findingsFiltered.map((f) => {
                const isOpen = expandedFinding === f.id;
                const evidenceCount = (data.evidence || []).filter((e) => e.finding_id === f.id).length;
                return (
                  <div key={f.id} className={`finding-card ${isOpen ? "open" : ""}`} data-testid={`finding-card-${f.id}`}>
                    <article className="finding-row">
                      <div className={`severity-dot ${f.severity}`}/>
                      <div className="finding-main">
                        <div className="finding-title">
                          <strong>{f.title}</strong>
                          <span className={statusClass(f.status)}>{f.status}</span>
                          {f.authority_override && <span className="chip auth-override">Yerel kurum</span>}
                          {evidenceCount > 0 && <span className="chip evidence-chip" title="Kanıt sayısı"><Paperclip size={11}/> {evidenceCount}</span>}
                        </div>
                        {f.action_text && (
                          <div className="action-text" data-testid={`action-text-${f.id}`}>
                            <span className="action-label">Yapılması gereken:</span> {f.action_text}
                          </div>
                        )}
                        {f.question && (
                          <div className="action-question" data-testid={`question-${f.id}`}>
                            <span className="action-label">Soru:</span> {f.question}
                          </div>
                        )}
                        <small className="source-row">{f.source} · Paket: {f.rule_package_id}{f.quantity ? ` · Gereken: ${f.quantity} ${f.unit}` : ""}</small>
                      </div>
                      <div className="finding-actions">
                        <button className="icon-button" onClick={() => setEditFinding(f)} data-testid={`finding-edit-${f.id}`} title="Aksiyonu düzenle"><PenLine size={13}/></button>
                        <button className="icon-button" onClick={() => setExpandedFinding(isOpen ? null : f.id)} data-testid={`finding-toggle-${f.id}`} title="Kanıtlar">
                          {isOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                        </button>
                        <button className="confirm" onClick={() => verifyFinding(f.id, "confirm")} data-testid={`fdetail-confirm-${f.id}`}><Check size={13}/> Yapıldı</button>
                        <button className="warn-btn" onClick={() => verifyFinding(f.id, "correct")} data-testid={`fdetail-correct-${f.id}`} title="Sahada doğrulanmalı">?</button>
                        <button className="reject" onClick={() => verifyFinding(f.id, "reject")} data-testid={`fdetail-reject-${f.id}`} title="Yapılmadı"><X size={13}/></button>
                      </div>
                    </article>
                    {isOpen && <EvidencePanel finding={f} onToast={setToast} onChanged={load}/>}
                  </div>
                );
              })}
              {!findingsFiltered.length && <small className="ai-empty">Seçili filtrede bulgu yok.</small>}
            </div>
            <small className="legal-foot"><Check size={13}/> Mevzuat snapshot: {assessment.legislation_snapshot}</small>
          </section>
        )}

        {tab === "plans" && (
          <section className="section-block">
            <div className="section-title">
              <div><div className="eyebrow">KROKİ ÇALIŞMA ALANI</div><h2>{activePlan?.file_name || "Kroki yok"}</h2></div>
              {activePlan && <button className="secondary-btn" onClick={() => setAiPlan(activePlan)} data-testid="ai-launch-button"><Sparkles size={14}/> AI önerileri</button>}
            </div>
            {data.plans.length > 1 && (
              <div className="plan-switcher">
                {data.plans.map((p) => (
                  <button key={p.id} className={`chip ${activePlanId === p.id ? "active" : ""}`} onClick={() => setActivePlanId(p.id)} data-testid={`plan-chip-${p.id}`}>
                    <MapIcon size={12}/> {p.file_name}
                  </button>
                ))}
              </div>
            )}
            {!data.plans.length && (
              <div className="empty-plan">
                <ImagePlus size={28}/>
                <strong>Henüz kroki yüklenmemiş</strong>
                <small>PDF, PNG, JPG veya WEBP yükleyin ve interaktif pin katmanıyla işaretleyin.</small>
                <label className="primary-btn upload-button">
                  <Upload size={14}/> İlk krokiyi yükle
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleUpload}/>
                </label>
              </div>
            )}
            {activePlan && <PlanCanvas key={`${activePlan.id}-${canvasVersion}`} plan={activePlan} onToast={setToast}/>}
          </section>
        )}

        {tab === "members" && (
          <section className="section-block">
            <div className="section-title">
              <div><div className="eyebrow">ÜYELER · ORTAK DOĞRULAMA</div><h2>İkinci danışmanı davet et</h2></div>
            </div>
            <MembersPanel assessmentId={id} isOwner={isOwner} onToast={setToast}/>
            <small className="legal-foot"><Check size={13}/> Davet edilen e-posta sisteme kayıtlıysa anında erişim kazanır; değilse ilk girişte otomatik eklenir.</small>
          </section>
        )}

        {tab === "reports" && (
          <section className="section-block">
            <div className="section-title">
              <div><div className="eyebrow">REVİZYON GEÇMİŞİ</div><h2>{data.reports.length} rapor sürümü</h2></div>
              <button className="primary-btn" onClick={generateReport} disabled={generating || summary.total === 0 || (summary.missing + summary.field > 0)} data-testid="reports-generate">
                {generating ? <Loader2 className="spin" size={14}/> : <FileText size={14}/>} Yeni revizyon
              </button>
            </div>
            {!data.reports.length && (
              <div className="empty-plan">
                <Lock size={28}/>
                <strong>Henüz rapor üretilmedi</strong>
                <small>Tüm bulgular doğrulandığında taslak rapor üretebilirsiniz.</small>
              </div>
            )}
            <div className="report-list">
              {data.reports.map((r) => (
                <article key={r.id} className={`report-row ${r.signed ? "signed" : ""}`} data-testid={`report-row-${r.id}`}>
                  <div><strong>{r.code}{r.signed ? " · İMZALI" : ""}</strong><small>{(r.created_at || "").slice(0,19).replace("T"," ")} · {r.created_by_name}</small></div>
                  <small>Snapshot: {r.legislation}{r.authority_id && r.authority_id !== "none" ? ` · ${r.authority_id.toUpperCase()}` : ""}</small>
                  <small>{Math.round((r.signed_size || r.size)/1024)} KB · {(r.signatures||[]).length} imza{r.verify_code && <> · <a href={`/verify/${r.verify_code}`} target="_blank" rel="noreferrer" className="evidence-gps" data-testid={`verify-link-${r.id}`}>{r.verify_code}</a></>}</small>
                  <button className="secondary-btn" onClick={() => setSignReport(r)} data-testid={`sign-${r.id}`}><Pen size={14}/> İmzala</button>
                  <button className="secondary-btn" onClick={() => setShareReport(r)} data-testid={`share-${r.id}`}><Share2 size={14}/> Paylaş</button>
                  <button className="secondary-btn" onClick={() => downloadReport(r.id, r.code)} data-testid={`download-${r.id}`}><Download size={14}/> İndir</button>
                </article>
              ))}
            </div>
            <small className="legal-foot"><Check size={13}/> Her rapor üretildiği tarihteki mevzuat sürümünü korur ve geriye dönük değişmez.</small>
          </section>
        )}
      </div>

      {aiPlan && <PlanAiPanel plan={aiPlan} assessmentId={id} onClose={() => { setAiPlan(null); setCanvasVersion((v) => v + 1); load(); }} onToast={setToast}/>}
      {shareReport && <ShareModal report={shareReport} onClose={() => setShareReport(null)} onToast={setToast}/>}
      {signReport && <SignReportModal report={signReport} onClose={() => setSignReport(null)} onToast={setToast} onSigned={load}/>}
      {editFinding && <ActionEditModal finding={editFinding} assessmentId={id} onClose={() => setEditFinding(null)} onToast={setToast} onSaved={load}/>}
      {toast && <div className="toast" data-testid="detail-toast">{toast}<button onClick={() => setToast("")}><X size={14}/></button></div>}
    </div>
  );
}
